import { ApolloQueryResult } from 'apollo-client';
import { get } from 'lodash';
import { Epic } from 'redux-observable';
import { Observable } from 'rxjs';

import { Action } from '../actions';
import {
    UPDATE_GRID_BANNER_PRICE_CELL,
    updateGridBannerPrice,
    UpdateGridBannerPrice,
    UpdateGridBannerPriceCell
} from '../actions/banners-grid';
import { DummyAction, dummyAction } from '../actions/common';
import {
    deleteEditCellData,
    DeteleEditCellData,
    UpdateEditCellData,
    updateEditCellData
} from '../actions/grid-screen';
import { EntitiesQueryResponse } from '../api/grid/EntitiesDataProvider';
import { phraseIdFieldName } from '../declarations/filter/filter';
import { selectGridSourceParams } from '../selectors/gridSourceParams/gridSourceParams';
import { mapBannerPriceErrors } from '../service/BannerEditErrors/BannerEditErrors';
import { getBannerPriceDataToApi } from '../service/BannerHelpers/BannerHelpers';
import { ApiMethodExecutingError } from '../service/Errors';
import '../service/Rx';
import { EpicDeps } from '../service/StoreFactory';
import { BannersAdditionalFilter } from '../typings/domain';
import { FilterExpressionValueType, Operator } from '../typings/filter';
import { updateAdPrices } from '../typings/graphql-grid';
import { State } from '../typings/state';

type UpdateGridBannerPriceCellEpicObservable = Observable<
    | DummyAction
    | DeteleEditCellData
    | UpdateEditCellData
    | UpdateGridBannerPrice
>;

export const updateGridBannerPriceCellEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_GRID_BANNER_PRICE_CELL).mergeMap(
        (
            action: UpdateGridBannerPriceCell
        ): UpdateGridBannerPriceCellEpicObservable => {
            const state = store$.value;
            const { clientInfo } = store$.value.grid;
            const sourceParams = selectGridSourceParams(state);
            const { bannerPrice } = action.params;

            if (!sourceParams || !bannerPrice) {
                return Observable.of(dummyAction);
            }

            const cellKey = {
                ...action.cellKeyPartial,
                view: sourceParams.view
            };

            const requestData = {
                id: action.params.bannerId,
                price: getBannerPriceDataToApi(action.params.bannerPrice)
            };

            return Observable.fromPromise(
                deps.bannersMutationExecutor.updateAdPrice([requestData])
            )
                .mergeMap(
                    (
                        result: ApolloQueryResult<updateAdPrices>
                    ): UpdateGridBannerPriceCellEpicObservable => {
                        const errors = get(
                            result,
                            'data.updateAdPrices.validationResult.errors',
                            []
                        );

                        if (errors.length) {
                            return Observable.of(
                                updateEditCellData(cellKey, {
                                    isSaving: false,
                                    error: mapBannerPriceErrors([
                                        {
                                            path: '',
                                            params: null,
                                            code: 'API_ERROR'
                                        }
                                    ])
                                })
                            );
                        }

                        const campaignsCalcTypeFilter =
                            state.grid.campaignsCalcTypeFilter;

                        return Observable.fromPromise(
                            deps.entitiesDataProvider.fetchGridDataSegment(
                                {
                                    ...sourceParams,
                                    filter: [
                                        {
                                            field: phraseIdFieldName,
                                            operator: Operator.EQUALS,
                                            value: {
                                                type:
                                                    FilterExpressionValueType.numberSet,
                                                value: [action.params.bannerId]
                                            }
                                        }
                                    ],
                                    additionalFilter:
                                        BannersAdditionalFilter.ALL,
                                    campaignsCalcTypeFilter
                                },
                                clientInfo,
                                0,
                                1,
                                undefined
                            )
                        )
                            .mergeMap((data: EntitiesQueryResponse) => {
                                const status = get(
                                    data,
                                    'data.data[0].status',
                                    undefined
                                );

                                return Observable.concat(
                                    Observable.of(deleteEditCellData(cellKey)),
                                    Observable.of(
                                        updateGridBannerPrice(
                                            action.params,
                                            status
                                        )
                                    )
                                );
                            })
                            .catch(() => {
                                return Observable.of(dummyAction);
                            });
                    }
                )
                .catch((error: ApiMethodExecutingError) => {
                    return Observable.of(
                        updateEditCellData(cellKey, {
                            isSaving: false,
                            error: get(error, 'errors', '').length
                                ? mapBannerPriceErrors(error.getErrors())
                                : mapBannerPriceErrors([
                                      {
                                          path: '',
                                          params: null,
                                          code: 'API_ERROR'
                                      }
                                  ])
                        })
                    );
                });
        }
    );
};
